import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  UsersRound,
  TrendingUp,
  DollarSign,
  Users,
  Crown,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  AlertCircle,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'

// Mock data สำหรับ Leader Dashboard
const mockTeamData = {
  summary: {
    total_partners: 12,
    active_partners: 10,
    total_members: 458,
    total_commission: 125000,
    commission_this_month: 45000,
    growth_percent: 12.5,
  },
  partners: [
    { id: '1', username: 'partner001', name: 'สมชาย ใจดี', members: 85, commission: 32000, status: 'active', growth: 15.2 },
    { id: '2', username: 'partner002', name: 'วิชัย มั่งมี', members: 62, commission: 24500, status: 'active', growth: 8.5 },
    { id: '3', username: 'partner003', name: 'พิมพ์ใจ สวยงาม', members: 48, commission: 18200, status: 'active', growth: -3.2 },
    { id: '4', username: 'partner004', name: 'อนุชา รวยเร็ว', members: 41, commission: 15800, status: 'active', growth: 22.1 },
    { id: '5', username: 'partner005', name: 'สุภาพร สดใส', members: 38, commission: 14500, status: 'active', growth: 5.8 },
    { id: '6', username: 'partner006', name: 'ประยุทธ์ เก่งมาก', members: 35, commission: 12000, status: 'inactive', growth: 0 },
  ]
}

export function TeamOverview() {
  const [isLoading, setIsLoading] = useState(true)
  const [error, _setError] = useState<string | null>(null)
  const [teamData, _setTeamData] = useState(mockTeamData)
  const [isRefreshing, setIsRefreshing] = useState(false)
  
  // Suppress unused warnings - will be used when connecting to real API
  void _setError
  void _setTeamData

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  const handleRefresh = () => {
    setIsRefreshing(true)
    setTimeout(() => {
      setIsRefreshing(false)
    }, 1000)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <p className="text-lg font-medium mb-2">ไม่สามารถโหลดข้อมูลได้</p>
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <Button onClick={() => window.location.reload()}>ลองใหม่</Button>
      </div>
    )
  }

  const { summary, partners } = teamData

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Crown className="h-6 w-6 text-amber-500" />
            ภาพรวมทีม
          </h1>
          <p className="text-muted-foreground">ดูผลงานและรายได้ของ Partners ในทีม</p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
          <RefreshCw className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")} />
          รีเฟรช
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-indigo-500/10">
                <UsersRound className="h-5 w-5 text-indigo-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Partners ในทีม</p>
                <p className="text-2xl font-bold">{summary.total_partners}</p>
                <p className="text-xs text-muted-foreground">
                  Active: {summary.active_partners}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-500/10">
                <Users className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">สมาชิกรวมทั้งทีม</p>
                <p className="text-2xl font-bold">{summary.total_members.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-win/10">
                <DollarSign className="h-5 w-5 text-win" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">คอมมิชชั่นเดือนนี้</p>
                <p className="text-2xl font-bold text-win">{formatCurrency(summary.commission_this_month)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-500/10">
                <TrendingUp className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">การเติบโต</p>
                <div className="flex items-center gap-1">
                  <p className="text-2xl font-bold">{summary.growth_percent}%</p>
                  {summary.growth_percent > 0 ? (
                    <ArrowUpRight className="h-5 w-5 text-win" />
                  ) : (
                    <ArrowDownRight className="h-5 w-5 text-lose" />
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Partners Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Partners ในทีม</CardTitle>
          <CardDescription>รายชื่อและผลงานของ Partners ทั้งหมด</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Partner</TableHead>
                <TableHead className="text-center">สถานะ</TableHead>
                <TableHead className="text-right">สมาชิก</TableHead>
                <TableHead className="text-right">คอมมิชชั่น</TableHead>
                <TableHead className="text-right">การเติบโต</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {partners.map((partner) => (
                <TableRow key={partner.id} className="hover:bg-muted/50">
                  <TableCell>
                    <div>
                      <p className="font-medium">{partner.name}</p>
                      <p className="text-xs text-muted-foreground">@{partner.username}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge 
                      variant="secondary" 
                      className={cn(
                        "border-0",
                        partner.status === 'active' ? "bg-win/10 text-win" : "bg-muted text-muted-foreground"
                      )}
                    >
                      {partner.status === 'active' ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">{partner.members}</TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(partner.commission)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className={cn(
                      "flex items-center justify-end gap-1",
                      partner.growth > 0 ? "text-win" : partner.growth < 0 ? "text-lose" : "text-muted-foreground"
                    )}>
                      {partner.growth > 0 ? (
                        <ArrowUpRight className="h-4 w-4" />
                      ) : partner.growth < 0 ? (
                        <ArrowDownRight className="h-4 w-4" />
                      ) : null}
                      {partner.growth}%
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Team Performance */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">สัดส่วนผลงานในทีม</CardTitle>
          <CardDescription>Top performers ในทีมของคุณ</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {partners.slice(0, 5).map((partner, index) => {
            const maxCommission = Math.max(...partners.map(p => p.commission))
            const percentage = (partner.commission / maxCommission) * 100
            return (
              <div key={partner.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={cn(
                      "h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold",
                      index === 0 && "bg-amber-500 text-white",
                      index === 1 && "bg-slate-400 text-white",
                      index === 2 && "bg-orange-600 text-white",
                      index > 2 && "bg-muted text-muted-foreground"
                    )}>
                      {index + 1}
                    </div>
                    <span className="font-medium">{partner.name}</span>
                  </div>
                  <span className="font-semibold">{formatCurrency(partner.commission)}</span>
                </div>
                <Progress value={percentage} className="h-2" />
              </div>
            )
          })}
        </CardContent>
      </Card>
    </div>
  )
}
